import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/Navbar';
import ContactStrip from '../../components/ContactStrip';
import { useLang } from '../../context/LanguageContext';
import styles from './DeveloperPage.module.css';

const STACK = [
  { cat: 'Frontend',   items: ['React', 'TypeScript', 'CSS Modules', 'Material UI', 'React Native'] },
  { cat: 'Backend',    items: ['Node.js', 'Express', 'JWT', 'FastAPI', 'REST / GraphQL'] },
  { cat: 'Data & DB',  items: ['PostgreSQL', 'MongoDB', 'Redis', 'Prisma', 'Supabase'] },
  { cat: 'DevOps',     items: ['GitHub Actions', 'Vercel', 'Railway'] },
  { cat: 'Blockchain', items: ['Solidity', 'Web3.js'] },
];

const PROJECTS = [
  {
    name: 'ClaudeCurate',
    url: 'https://github.com/JoaquinPerezDev/claudecurate',
    live: 'https://claudecurate.vercel.app',
    desc_en: 'A lightweight task tracker that auto-completes your tasks when Claude finishes related work. Integrates with Claude Code CLI and Claude Desktop via MCP. Fully responsive across all devices.',
    desc_es: 'Un gestor de tareas ligero que completa automáticamente tus tareas cuando Claude termina el trabajo relacionado. Se integra con Claude Code CLI y Claude Desktop vía MCP. Totalmente responsive en todos los dispositivos.',
    tech: ['React 18', 'Material UI', 'Node.js', 'Express', 'MongoDB', 'JWT', 'MCP', 'Responsive'],
  },
  {
    name: 'joaquinmontes.com',
    url: null,
    live: null,
    desc_en: 'A multi-profile portfolio with 5 distinct pages, each with its own design language. Built with React, CSS Modules, and a custom bilingual context system. Fully responsive across all devices.',
    desc_es: 'Un portfolio multi-perfil con 5 páginas distintas, cada una con su propio lenguaje visual. Construido con React, CSS Modules y un sistema bilingüe personalizado. Totalmente responsive en todos los dispositivos.',
    tech: ['React 18', 'React Router', 'CSS Modules', 'Bilingual Context', 'Responsive', '5 Pages'],
  },
];

// ── SMART SCROLL CASCADE ───────────────────────────────────────
// Same logic as Actor page: IntersectionObserver on slow scroll,
// velocity detector auto-fires skipped elements on fast scroll.
function useSmartCascade(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const VELOCITY_THRESHOLD = 800;
    const FAST_STAGGER = 60;
    const elements = Array.from(ref.current.querySelectorAll('[data-cascade]'));
    const revealed = new Set();

    const reveal = (el) => {
      if (revealed.has(el)) return;
      revealed.add(el);
      el.classList.add(styles.cascadeVisible);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => { if (entry.isIntersecting) reveal(entry.target); });
    }, { threshold: 0.15 });

    elements.forEach(el => observer.observe(el));

    let lastY = window.scrollY;
    let lastTime = performance.now();
    let fastTimer = null;

    const onScroll = () => {
      const now = performance.now();
      const dy = Math.abs(window.scrollY - lastY);
      const dt = now - lastTime;
      const velocity = (dy / dt) * 1000;
      lastY = window.scrollY;
      lastTime = now;

      if (velocity > VELOCITY_THRESHOLD) {
        clearTimeout(fastTimer);
        fastTimer = setTimeout(() => {
          const viewportBottom = window.scrollY + window.innerHeight;
          const skipped = elements.filter(el => {
            if (revealed.has(el)) return false;
            return el.getBoundingClientRect().top + window.scrollY < viewportBottom;
          });
          skipped.forEach((el, i) => setTimeout(() => reveal(el), i * FAST_STAGGER));
        }, 150);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      clearTimeout(fastTimer);
    };
  }, [ref]);
}

function TypeWriter({ text, speed = 40 }) {
  const [display, setDisplay] = useState('');
  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      setDisplay(text.slice(0, i + 1));
      i++;
      if (i >= text.length) clearInterval(timer);
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed]);
  return <span>{display}<span className={styles.cursor}>▋</span></span>;
}


// ── SCROLL INDICATOR ──────────────────────────────────────────
function ScrollIndicator() {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const onScroll = () => setHidden(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const handleClick = () => window.scrollBy({ top: window.innerHeight * 0.6, behavior: 'smooth' });
  return (
    <div className={`${styles.scrollIndicator} ${hidden ? styles.hidden : ''}`} onClick={handleClick}>
      <span className={styles.scrollGlyph}>↓</span>
      <span className={styles.scrollLabel}>scroll</span>
    </div>
  );
}

export default function DeveloperPage() {
  const { t, lang } = useLang();
  const cascadeRef = useRef(null);
  useSmartCascade(cascadeRef);

  return (
    <>
      <Navbar theme="dark" wordmark="joaquin@dev ~ %" />
      <div className={styles.root}>
      <div className={styles.scanline} />

      {/* HERO — no cascade, appears immediately */}
      <section className={styles.hero}>
        <div className={styles.heroCode}>
          <pre className={styles.codeBlock}>{`const joaquin = {
  role: "Full Stack Developer",
  focus: [
    "Web Apps",
    "Blockchain",
    "Mobile"
  ],
  location: "USA · Remote",
  available: true
};`}</pre>
        </div>
        <div className={styles.heroText}>
          <p className={styles.eyebrow}>{t('dev_eyebrow')}</p>
          <h1 className={styles.name}><TypeWriter text="Joaquin Montes" speed={60} /></h1>
          <p className={styles.bio}>{t('dev_bio')}</p>
        </div>
        <ScrollIndicator />
      </section>

      {/* Everything below hero gets cascade animations */}
      <div ref={cascadeRef}>

        {/* STACK */}
        <section className={styles.section}>
          <p data-cascade className={`${styles.sectionTitle} ${styles.cascade} ${styles.cascadeTitle}`}>
            {t('dev_stack')}
          </p>
          <div className={styles.stackGrid}>
            {STACK.map((s, i) => (
              <div key={i} data-cascade className={`${styles.stackCol} ${styles.cascade}`}>
                <p className={styles.stackCat}>{s.cat}</p>
                <ul className={styles.stackList}>
                  {s.items.map((item, j) => (
                    <li key={j} className={styles.stackItem}>
                      <span className={styles.prompt}>›</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* PROJECTS */}
        <section className={styles.section}>
          <p data-cascade className={`${styles.sectionTitle} ${styles.cascade} ${styles.cascadeTitle}`}>
            {t('dev_projects')}
          </p>
          <div className={styles.projects}>
            {PROJECTS.map((p, i) => (
              <div key={i} data-cascade className={`${styles.projectCard} ${styles.cascade}`}>
                <div className={styles.projectTop}>
                  <span className={styles.projectIndex}>0{i + 1}</span>
                  <h3 className={styles.projectName}>{p.name}</h3>
                </div>
                <p className={styles.projectDesc}>{lang === 'es' ? p.desc_es : p.desc_en}</p>
                <div className={styles.projectTags}>
                  {p.tech.map((tech, j) => <span key={j} className={styles.tag}>{tech}</span>)}
                </div>
                <div className={styles.projectLinks}>
                  {p.url && (
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className={styles.projectLink}>
                      GitHub →
                    </a>
                  )}
                  {p.live && (
                    <a href={p.live} target="_blank" rel="noopener noreferrer" className={styles.projectLink}>
                      Live →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

      </div>

      <ContactStrip theme="dark" emailParts={["Joaquinmontesfilm", "@", "gmail.com"]} />
    </div>
    </>
  );
}
